// frontend/src/pages/OptimizationEntityNavigator.tsx

import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  useQuery,
} from '@tanstack/react-query';

import {
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { currentOrigin, withReturnTo } from '../modules/pricingJourney/navigation';

import SearchRoundedIcon
  from '@mui/icons-material/SearchRounded';

import ArrowForwardRoundedIcon
  from '@mui/icons-material/ArrowForwardRounded';

import {
  getMarginCenterExplorer,
} from '../modules/marginCenter/services/marginCenterService';


type EntityKind =
  | 'customer'
  | 'product';


type EntityOption = {
  kind: EntityKind;
  id: string;
  label: string;
};


export default function OptimizationEntityNavigator() {
  const navigate = useNavigate();
  const location = useLocation();

  const [input, setInput] =
    useState('');

  const [debouncedInput, setDebouncedInput] =
    useState('');

  const [selected, setSelected] =
    useState<EntityOption | null>(null);

  useEffect(() => {
    const timer =
      window.setTimeout(
        () =>
          setDebouncedInput(
            input.trim(),
          ),
        250,
      );

    return () =>
      window.clearTimeout(timer);
  }, [input]);

  useEffect(() => {
    setSelected(null);
  }, [debouncedInput]);

  const searchEnabled =
    debouncedInput.length >= 2;

  const customerQuery =
    useQuery({
      queryKey: [
        'optimization-center',
        'entity-search',
        'customer',
        debouncedInput,
      ],
      queryFn: () =>
        getMarginCenterExplorer({
          dimension: 'customer',
          search: debouncedInput,
          comparison_mode: 'none',
          metric: 'revenue',
          sort_field: 'entity_label',
          sort_direction: 'asc',
          limit: 10,
        }),
      enabled: searchEnabled,
      staleTime: 30_000,
      retry: 1,
    });

  const productQuery =
    useQuery({
      queryKey: [
        'optimization-center',
        'entity-search',
        'product',
        debouncedInput,
      ],
      queryFn: () =>
        getMarginCenterExplorer({
          dimension: 'product',
          search: debouncedInput,
          comparison_mode: 'none',
          metric: 'revenue',
          sort_field: 'entity_label',
          sort_direction: 'asc',
          limit: 10,
        }),
      enabled: searchEnabled,
      staleTime: 30_000,
      retry: 1,
    });

  const options =
    useMemo<EntityOption[]>(
      () => [
        ...(
          customerQuery.data?.rows
          ?? []
        ).map((row) => ({
          kind:
            'customer' as const,
          id: row.entity.key,
          label:
            row.entity.label
            || row.entity.key,
        })),
        ...(
          productQuery.data?.rows
          ?? []
        ).map((row) => ({
          kind:
            'product' as const,
          id: row.entity.key,
          label:
            row.entity.label
            || row.entity.key,
        })),
      ],
      [
        customerQuery.data,
        productQuery.data,
      ],
    );

  const isFetching =
    customerQuery.isFetching
    || productQuery.isFetching;

  const bothFailed =
    searchEnabled
    && customerQuery.isError
    && productQuery.isError;

  const openSelected = () => {
    if (!selected) return;

    const target =
      selected.kind === 'customer'
        ? `/margin-center/customers/${
            encodeURIComponent(
              selected.id,
            )
          }`
        : `/margin-center/products/${
            encodeURIComponent(
              selected.id,
            )
          }`;

    // PRC-027: el origen va en la dirección para que sobreviva a los cambios de la ficha.
    navigate(withReturnTo(target, currentOrigin(location)));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: {
          xs: 1.5,
          md: 1.75,
        },
        borderRadius: 2.5,
        border:
          '1px solid rgba(56,189,248,0.22)',
        bgcolor:
          'rgba(14,33,51,0.48)',
        backgroundImage: 'none',
      }}
    >
      <Stack
        direction={{
          xs: 'column',
          md: 'row',
        }}
        spacing={1.25}
        alignItems={{
          xs: 'stretch',
          md: 'center',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            minWidth: {
              md: 190,
            },
          }}
        >
          <SearchRoundedIcon
            sx={{
              color: '#7dd3fc',
              fontSize: 20,
            }}
          />

          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                color: '#f8fafc',
                fontWeight: 850,
              }}
            >
              Ir a cliente o producto
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: '#7890a5',
              }}
            >
              Abre su contexto comercial
            </Typography>
          </Box>
        </Stack>

        <Autocomplete<
          EntityOption,
          false,
          false,
          false
        >
          options={options}
          value={selected}
          inputValue={input}
          loading={isFetching}
          filterOptions={(items) =>
            items
          }
          groupBy={(option) =>
            option.kind === 'customer'
              ? 'Clientes'
              : 'Productos'
          }
          getOptionLabel={(option) =>
            `${
              option.label
            } · ${
              option.id
            }`
          }
          isOptionEqualToValue={(
            option,
            value,
          ) =>
            option.kind
              === value.kind
            && option.id
              === value.id
          }
          noOptionsText={
            !searchEnabled
              ? 'Escribe al menos 2 caracteres'
              : isFetching
                ? 'Buscando…'
                : 'Sin resultados'
          }
          onInputChange={(
            _event,
            value,
          ) => setInput(value)}
          onChange={(
            _event,
            value,
          ) => setSelected(value)}
          sx={{
            flex: 1,
            minWidth: 0,
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Buscar cliente o producto"
              placeholder="Nombre o identificador"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isFetching && (
                      <CircularProgress
                        size={16}
                      />
                    )}
                    {
                      params
                        .InputProps
                        .endAdornment
                    }
                  </>
                ),
              }}
            />
          )}
        />

        <Button
          variant="contained"
          endIcon={
            <ArrowForwardRoundedIcon />
          }
          disabled={!selected}
          onClick={openSelected}
          sx={{
            minWidth: {
              md: 155,
            },
            textTransform: 'none',
            fontWeight: 850,
            bgcolor: '#67dafa',
            color: '#062035',
            '&:hover': {
              bgcolor: '#9deaff',
            },
          }}
        >
          {selected?.kind
            === 'product'
            ? 'Abrir producto'
            : 'Abrir cliente'}
        </Button>
      </Stack>

      {bothFailed && (
        <Alert
          severity="warning"
          sx={{ mt: 1.25 }}
        >
          No se pudo consultar el catálogo.
        </Alert>
      )}
    </Paper>
  );
}
